import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowRightLong, FaEye } from 'react-icons/fa6';
import { useSession } from '../../context/SessionContext';
import productServices from '../../services/ProductsServices';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import utils from '../../utils';

function CategoryProducts() {
    const { productTypes, hasProductTypes } = useSession();
    const [productsByCategory, setProductsByCategory] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate()

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const categories = hasProductTypes ? productTypes : [];

    useEffect(() => {
        const loadProductsByCategory = async () => {
            let finalCategories = categories;
            if (!hasProductTypes) {
                try {
                    const res = await productServices.getProductTypes();
                    finalCategories = res.data.results || res.data;
                } catch (err) {
                    setError('Failed to load categories');
                    return;
                }
            }

            if (finalCategories.length === 0) return;

            finalCategories = [...finalCategories]
                .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
                .slice(0, 3);

            setLoading(true);
            setError(null);

            try {
                const promises = finalCategories.map(async (category) => {
                    try {
                        const res = await productServices.getProducts(`?product_type=${category.id}`);
                        return { categoryId: category.id, data: res.data };
                    } catch (err) {
                        console.error(`Failed to load products for ${category.name}`, err);
                        return { categoryId: category.id, data: { results: [] } };
                    }
                });

                const results = await Promise.all(promises);
                const productsMap = {};
                results.forEach(({ categoryId, data }) => {
                    productsMap[categoryId] = data;
                });

                setProductsByCategory(productsMap);
            } catch (err) {
                setError(err?.message || 'Failed to load products');
            } finally {
                setLoading(false);
            }
        };

        loadProductsByCategory();
    }, [hasProductTypes, categories]);

    if (loading && categories.length > 0) {
        return (
            <div className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
                        <p className="mt-4 text-gray-600">Loading products...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <p className="text-red-500 font-semibold">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-4 bg-secondary text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show "loading categories" only if session hasn’t hydrated yet
    if (!hasProductTypes) {
        return (
            <div className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <p className="text-gray-600">Preparing categories...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <div className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <p className="text-gray-500 font-semibold">No categories available</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-9 bg-gray-50 dark:bg-gray-900">
            {categories.map((category) => {
                const products = productsByCategory[category.id];

                // Skip categories with no products (or still loading)
                if (!products?.results?.length) return null;

                return (
                    <section key={category.id} className="py-7 mb-6">
                        <div className="container mx-auto">
                            <div className="text-center mb-8">
                                <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                                    {category.name}
                                </h2>
                                <div className="flex justify-center items-center">
                                    <Link
                                        to={`/products?product_type=${category.id}`}
                                        className="flex gap-3 mt-1.5 text-gray-600 hover:text-secondary transition-colors items-center"
                                    >
                                        View All {category.name} <FaArrowRightLong />
                                    </Link>
                                </div>
                                <div className="mt-3 mx-auto w-20 h-1.5 bg-secondary rounded-full"></div>
                            </div>

                            <Swiper
                                modules={[Navigation, Pagination]}
                                spaceBetween={12}
                                slidesPerView={2}
                                pagination={{ clickable: true }}
                                navigation
                                speed={600}
                                breakpoints={{
                                    640: { slidesPerView: 2, spaceBetween: 20 },
                                    768: { slidesPerView: 3, spaceBetween: 24 },
                                    1024: { slidesPerView: 4, spaceBetween: 32 },
                                }}
                                className="pb-12 w-full"
                                style={{ maxWidth: '96vw' }}
                            >
                                {products.results?.map((product) => (
                                    <SwiperSlide key={product.id || product.slug}>
                                        <div className="group rounded-lg overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                                            <div
                                                onClick={() => navigate(`/product/${product.slug}`)}
                                                className="relative overflow-hidden">
                                                <img
                                                    alt={product.name}
                                                    className="w-full h-36 lg:h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                                                    src={product.image}
                                                />
                                                {product?.is_featured ? (
                                                    <span className="absolute top-3 left-3 bg-gradient-to-r from-primary to-secondary text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase z-10 shadow-lg">
                                                        Featured
                                                    </span>
                                                ) : product?.price >= 4000 ? (
                                                    <span className="absolute top-3 left-3 bg-gradient-to-r from-accent to-info text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase z-10 shadow-lg">
                                                        Premium
                                                    </span>
                                                ) : null}
                                                <Link
                                                    to={`/product/${product?.slug}`}
                                                    className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-800 hover:bg-secondary hover:text-white transition-all opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 shadow-md z-10"
                                                >
                                                    <FaEye className="w-4 h-4" />
                                                </Link>
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                                            </div>
                                            <div className="p-3 lg:p-6 text-xs md:text-base bg-gray-800 dark:bg-slate-800 text-white flex flex-col items-start transition-colors duration-300 group-hover:bg-gray-900">
                                                <h3
                                                    className="font-bold text-sm text-start w-full truncate"
                                                >
                                                    {product?.name}
                                                </h3>
                                                <p className="text-secondary font-bold mt-auto">
                                                    KES {utils.toCommas(product?.price || 0)}
                                                </p>
                                                <Link
                                                    className="mt-3 lg:mt-4 inline-block bg-secondary text-center text-xs w-full text-white font-bold lg:py-2.5 py-1.5 lg:px-6 px-3 rounded-lg hover:bg-orange-600 hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                                    to={`/product/${product.slug}`}
                                                >
                                                    VIEW PRODUCT
                                                    <span className="group-hover/btn:translate-x-1 transition-transform duration-300 ml-2">→</span>
                                                </Link>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </section>
                );
            })}
        </div >
    );
}

export default CategoryProducts;